/**
 * Whop server-side event tracking
 *
 * POSTs to /trigger-whop-event → WordPress action=trigger_whop_event
 * Wired from everflowConversions the same way Meta/TikTok are.
 */

import { logger } from './logger'
import { getFunnelIdFromPath } from './funnelUtils'
import { sessionStore } from './sessionKey'
import { trackWhopEvent, whenWhopPixelReady, whenWhopFingerprintReady, getCapturedWhopFingerprint } from './whopPixel'

export const WHOP_EVENT_QUIZ_STARTED = 'quiz_started'
export const WHOP_EVENT_EMAIL_PHONE_CAPTURED = 'phone_capture'
export const WHOP_EVENT_QUIZ_REJECTED = 'quiz_rejected'
export const WHOP_EVENT_QUIZ_COMPLETED = 'quiz_completed'
export const WHOP_EVENT_ADD_PAYMENT_INFO = 'add_payment_info'
export const WHOP_EVENT_ADD_PAYMENT_INFO_UPSELL = 'add_payment_info_upsell'
export const WHOP_EVENT_PRODUCT_SELECTION = 'product_selection'
export const WHOP_EVENT_PRODUCT_SELECTION_UPSELL = 'product_selection_upsell'
export const WHOP_EVENT_PLAN_SELECTION = 'plan_selection'
export const WHOP_EVENT_PLAN_SELECTION_UPSELL = 'plan_selection_upsell'
export const WHOP_EVENT_PURCHASE = 'purchase'
export const WHOP_EVENT_PURCHASE_UPSELL = 'purchase_upsell'

const WHOP_UPSELL_PRODUCT_NAME_KEY = 'whop_upsell_product_name'
const WHOP_UPSELL_AMOUNT_KEY = 'whop_upsell_amount'
const WHOP_UPSELL_PLAN_KEY = 'whop_upsell_plan'
const UPSELL_AMOUNT_KEY = 'upsell_amount'

/** Latest plan chosen on an upsell page. Async selection must not overwrite it. */
const latestUpsellSelectionByOffer = new Map()

const getUpsellAmountSessionKey = (offerId) =>
  offerId !== null && offerId !== undefined
    ? `${UPSELL_AMOUNT_KEY}_offer_${offerId}`
    : UPSELL_AMOUNT_KEY

const parseUpsellAmount = (amount) => {
  if (amount === null || amount === undefined || amount === '') return null
  const parsed = parseFloat(Number(amount).toFixed(2))
  if (Number.isNaN(parsed) || parsed <= 0) return null
  return parsed
}

/**
 * Latest upsell bundle total. Defaults to the monthly total until a plan is selected.
 * Offer-scoped so NAD, Sermorelin, and Zofran do not overwrite each other.
 */
export const rememberUpsellAmount = (amount, offerId = null, { overwrite = true } = {}) => {
  const parsed = parseUpsellAmount(amount)
  if (parsed == null) return
  const value = String(parsed)
  const offerKey = getUpsellAmountSessionKey(offerId)
  if (!overwrite && sessionStore.getItem(offerKey)) return
  sessionStore.setItem(offerKey, value)
  sessionStore.setItem(UPSELL_AMOUNT_KEY, value)
}

export const readUpsellAmount = (offerId = null) => {
  const candidates = [
    offerId !== null && offerId !== undefined
      ? sessionStore.getItem(getUpsellAmountSessionKey(offerId))
      : null,
    sessionStore.getItem(UPSELL_AMOUNT_KEY),
  ]

  for (const candidate of candidates) {
    const parsed = parseUpsellAmount(candidate)
    if (parsed != null) return parsed
  }

  return null
}

export const getWhopUpsellProductSessionKey = (offerId) =>
  offerId !== null && offerId !== undefined
    ? `${WHOP_UPSELL_PRODUCT_NAME_KEY}_offer_${offerId}`
    : WHOP_UPSELL_PRODUCT_NAME_KEY

export const getWhopUpsellAmountSessionKey = (offerId) =>
  offerId !== null && offerId !== undefined
    ? `${WHOP_UPSELL_AMOUNT_KEY}_offer_${offerId}`
    : WHOP_UPSELL_AMOUNT_KEY

export const getWhopUpsellPlanSessionKey = (offerId) =>
  offerId !== null && offerId !== undefined
    ? `${WHOP_UPSELL_PLAN_KEY}_offer_${offerId}`
    : WHOP_UPSELL_PLAN_KEY

/**
 * Persist the upsell product name from product_selection_upsell so later
 * add_payment_info_upsell / purchase_upsell can reuse it (funnelData still
 * holds the main checkout product name).
 */
export const rememberWhopUpsellProductName = (productName, offerId = null) => {
  if (!productName || productName === 'unknown') return
  sessionStore.setItem(getWhopUpsellProductSessionKey(offerId), productName)
  sessionStore.setItem(WHOP_UPSELL_PRODUCT_NAME_KEY, productName)
}

/**
 * Persist the upsell monetary amount so later add_payment_info_upsell can
 * reuse it (funnelData still holds the main checkout price).
 */
export const rememberWhopUpsellAmount = (amount, offerId = null, { overwrite = true } = {}) => {
  if (amount === null || amount === undefined || amount === '') return
  const parsed = parseFloat(Number(amount).toFixed(2))
  if (Number.isNaN(parsed) || parsed < 0) return
  const value = String(parsed)
  const offerKey = getWhopUpsellAmountSessionKey(offerId)
  if (!overwrite && sessionStore.getItem(offerKey)) return
  sessionStore.setItem(offerKey, value)
  sessionStore.setItem(WHOP_UPSELL_AMOUNT_KEY, value)
}

/**
 * Write the selected plan's bundle total into upsell and Whop session keys
 * as soon as the plan changes.
 */
export const rememberSelectedUpsellPlan = (plan, offerId = null) => {
  if (!plan) return
  const amount = plan.totalPrice ?? plan.price ?? null
  const planId = plan.id || null
  latestUpsellSelectionByOffer.set(String(offerId ?? ''), {
    planId,
    amount: parseUpsellAmount(amount),
  })
  rememberUpsellAmount(amount, offerId)
  rememberWhopUpsellAmount(amount, offerId)
  if (planId) rememberWhopUpsellPlan(planId, offerId)
}

/**
 * True when this amount/plan is older than the plan the shopper just selected.
 */
export const isStaleUpsellSessionWrite = (offerId, { planId = null, amount = null } = {}) => {
  const latest = latestUpsellSelectionByOffer.get(String(offerId ?? ''))
  if (!latest) return false
  if (planId && latest.planId && planId !== latest.planId) return true
  const parsed = parseUpsellAmount(amount)
  if (parsed != null && latest.amount != null && parsed !== latest.amount) return true
  return false
}

/**
 * Resolve upsell product name for Whop product_id.
 * Prefer explicit override → offer-scoped session → last upsell session → funnelData.
 */
export const resolveWhopUpsellProductName = (funnelData = {}, options = {}) => {
  const { productName = null, offerId = null } = options
  if (productName && productName !== 'unknown') return productName

  if (offerId !== null && offerId !== undefined) {
    const byOffer = sessionStore.getItem(getWhopUpsellProductSessionKey(offerId))
    if (byOffer) return byOffer
  }

  const lastUpsell = sessionStore.getItem(WHOP_UPSELL_PRODUCT_NAME_KEY)
  if (lastUpsell) return lastUpsell

  return (
    funnelData.selected_product_name ||
    funnelData.chosen_medicine_name ||
    funnelData.selected_product_id ||
    ''
  )
}

/**
 * Resolve upsell amount for Whop value.
 * Prefer explicit override → offer-scoped session → last upsell session → null
 * (do not fall back to main-checkout funnelData prices).
 */
export const resolveWhopUpsellAmount = (funnelData = {}, options = {}) => {
  const { amount = null, offerId = null } = options

  const candidates = [
    amount,
    offerId !== null && offerId !== undefined
      ? sessionStore.getItem(getWhopUpsellAmountSessionKey(offerId))
      : null,
    sessionStore.getItem(WHOP_UPSELL_AMOUNT_KEY),
    funnelData.amount,
  ]

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue
    const parsed = parseFloat(Number(candidate).toFixed(2))
    if (!Number.isNaN(parsed) && parsed >= 0) return parsed
  }

  return null
}

/**
 * Persist the upsell plan so later product_selection_upsell /
 * add_payment_info_upsell / purchase_upsell can reuse it (funnelData still
 * holds the main checkout selected_plan).
 */
export const rememberWhopUpsellPlan = (plan, offerId = null) => {
  if (!plan || plan === 'unknown') return
  sessionStore.setItem(getWhopUpsellPlanSessionKey(offerId), plan)
  sessionStore.setItem(WHOP_UPSELL_PLAN_KEY, plan)
}

/**
 * Resolve upsell plan for Whop plan_id.
 * Prefer explicit override → offer-scoped session → last upsell session → null
 * (do not fall back to main-checkout funnelData.selected_plan).
 */
export const resolveWhopUpsellPlan = (funnelData = {}, options = {}) => {
  const { planType = null, offerId = null } = options

  const candidates = [
    planType,
    offerId !== null && offerId !== undefined
      ? sessionStore.getItem(getWhopUpsellPlanSessionKey(offerId))
      : null,
    sessionStore.getItem(WHOP_UPSELL_PLAN_KEY),
  ]

  for (const candidate of candidates) {
    if (candidate && candidate !== 'unknown') return candidate
  }

  return null
}

const getCookie = (name) => {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : ''
}

const getUrlParam = (name) => {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get(name) || ''
}

const getFunnelUrl = () => {
  if (typeof window === 'undefined') return ''
  return window.location.href
}

const getFunnelVariant = (funnelData = {}) => {
  return (
    funnelData.funnel_id ||
    getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '') ||
    ''
  )
}

/**
 * product_id in the Whop schema carries the product name when available.
 */
const getProductName = (funnelData = {}) => {
  return (
    funnelData.selected_product_name ||
    funnelData.chosen_medicine_name ||
    funnelData.selected_product_id ||
    ''
  )
}

/**
 * Monetary value associated with the event.
 * Prefer explicit amount / total_prescription_price / selected_product_price.
 */
const getEventValue = (funnelData = {}) => {
  const candidates = [
    funnelData.amount,
    funnelData.total_prescription_price,
    funnelData.selected_product_price,
    funnelData.display_order_total,
    funnelData.chosen_medicine_price,
  ]

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue
    const parsed =
      typeof candidate === 'number'
        ? candidate
        : parseFloat(String(candidate).replace(/[$,]/g, ''))
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return parseFloat(parsed.toFixed(2))
    }
  }

  return null
}

/**
 * plan_id in the Whop schema carries the selected plan name when available.
 */
const getPlanName = (funnelData = {}) => {
  const plan = funnelData.selected_plan || funnelData.plan_id || ''
  if (!plan || plan === 'unknown') return ''

  const planLabel = {
    monthly: '1-Month Plan',
    quarterly: '3-Month Plan',
    sixMonth: '6-Month Plan',
    annual: '12-Month Plan',
  }[plan]

  return planLabel || plan
}

const buildContext = () => {
  const context = {
    fbc: getCookie('_fbc'),
    fbclid: getUrlParam('fbclid'),
    fbp: getCookie('_fbp'),
    gclid: getUrlParam('gclid'),
    gclsrc: getUrlParam('gclsrc'),
    // ip_address is intentionally omitted client-side — WP should set it from the request
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    screen_resolution:
      typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : '',
    language: typeof navigator !== 'undefined' ? navigator.language || '' : '',
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || ''
        : '',
    utm_campaign: getUrlParam('utm_campaign'),
    utm_content: getUrlParam('utm_content'),
    utm_id: getUrlParam('utm_id'),
    utm_medium: getUrlParam('utm_medium'),
    utm_source: getUrlParam('utm_source'),
    utm_term: getUrlParam('utm_term'),
  }

  // Fingerprint comes from Whop's own t.whop.tw/conversions payload (same value)
  const { fingerprint, fingerprint_confidence } = getCapturedWhopFingerprint()
  if (fingerprint) {
    context.fingerprint = fingerprint
  }
  if (fingerprint_confidence != null) {
    context.fingerprint_confidence = fingerprint_confidence
  }

  const ga = getCookie('_ga')
  if (ga) {
    context.ga = ga
  }

  return context
}

const getWhopAnonymousId = () => {
  const fromCookie = getCookie('_wuid')
  if (fromCookie) return fromCookie

  try {
    if (typeof localStorage !== 'undefined') {
      const fromStorage = localStorage.getItem('_wuid')
      if (fromStorage) return fromStorage
    }
  } catch (_) {
    // ignore storage access errors
  }

  return ''
}

const buildUserPayload = (funnelData = {}) => {
  const user = {
    // Whop pixel sets `_wuid` cookie/localStorage after s.js loads
    anonymous_id: getWhopAnonymousId(),
  }

  const linkedAnonymousId = getCookie('_wuid_link')
  if (linkedAnonymousId) {
    user.linked_anonymous_id = linkedAnonymousId
  }

  const city = funnelData.shipping_city || funnelData.city || ''
  if (city) user.city = city

  const country = funnelData.shipping_country || funnelData.country || ''
  if (country) user.country = country

  if (funnelData.email) user.email = funnelData.email

  const postalCode =
    funnelData.shipping_postal_code ||
    funnelData.postal_code ||
    funnelData.zip ||
    ''
  if (postalCode) user.postal_code = postalCode

  const state = funnelData.shipping_state || funnelData.state || ''
  if (state) user.state = state

  return user
}

/**
 * Build the Whop trigger_whop_event request body.
 * @param {string} eventName
 * @param {Object} funnelData
 * @returns {Object}
 */
export const buildWhopEventPayload = (eventName, funnelData = {}) => {
  const funnelUrl = getFunnelUrl()
  const title = getFunnelVariant(funnelData)
  const productId = getProductName(funnelData)
  const value = getEventValue(funnelData)
  const planId = getPlanName(funnelData)

  const payload = {
    event_name: eventName,
    context: buildContext(),
    action_source: 'app',
    currency: 'usd',
    event_time: new Date().toISOString(),
    referrer_url: typeof document !== 'undefined' ? (document.referrer || '') : '',
    source: title,
    title,
    url: funnelUrl,
    user: buildUserPayload(funnelData),
  }

  if (productId) {
    payload.product_id = productId
  }

  if (planId) {
    payload.plan_id = planId
  }

  if (value !== null) {
    payload.value = value
  }

  return payload
}

/**
 * Serialize Whop server API calls with a 1s gap between each request.
 */
const WHOP_API_DELAY_MS = 1000
const whopApiQueue = []
let whopApiProcessing = false
let lastWhopApiAt = 0

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const enqueueWhopApiCall = (sendFn) =>
  new Promise((resolve) => {
    whopApiQueue.push({ sendFn, resolve })
    processWhopApiQueue()
  })

const processWhopApiQueue = async () => {
  if (whopApiProcessing) return
  whopApiProcessing = true

  // Wait for s.js, then for Whop's own /conversions fingerprint
  await whenWhopPixelReady()
  await whenWhopFingerprintReady()

  while (whopApiQueue.length > 0) {
    const waitMs = Math.max(0, WHOP_API_DELAY_MS - (Date.now() - lastWhopApiAt))
    if (waitMs > 0) {
      await sleep(waitMs)
    }

    const { sendFn, resolve } = whopApiQueue.shift()
    lastWhopApiAt = Date.now()

    try {
      resolve(await sendFn())
    } catch (error) {
      logger.error('[Whop] queued API call failed:', error)
      resolve(undefined)
    }
  }

  whopApiProcessing = false
}

/**
 * POST a Whop event to WordPress (action=trigger_whop_event).
 * Also mirrors the event on the browser pixel when available.
 * Server requests are queued with a 1s delay between each.
 *
 * @param {string} eventName
 * @param {Object} funnelData
 * @returns {Promise<Object|undefined>}
 */
export const triggerWhopEvent = async (eventName, funnelData = {}) => {
  if (!eventName) {
    logger.warn('[Whop] Skipping — missing event_name')
    return
  }

  // Browser pixel (best-effort; does not block / wait on the API queue)
  // try {
  //   trackWhopEvent(eventName)
  // } catch (_) {
  //   // ignore — pixel may not be loaded yet
  // }

  return enqueueWhopApiCall(async () => {
    try {
      // Build after pixel ready so `_wuid` cookie is available for anonymous_id
      const payload = buildWhopEventPayload(eventName, funnelData)
      logger.debug('[Whop] Sending queued trigger_whop_event:', eventName, payload)

      const response = await fetch('/trigger-whop-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      let result
      try {
        result = await response.json()
      } catch (_) {
        result = null
      }

      if (!response.ok) {
        logger.warn('[Whop] trigger_whop_event server error:', result || response.status)
        return
      }

      logger.debug('[Whop] trigger_whop_event success:', result)
      return result
    } catch (error) {
      logger.error('[Whop] trigger_whop_event fetch error:', error)
      // Never throw — must not interrupt funnel flow
    }
  })
}

export const trackWhopStartQuiz = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_QUIZ_STARTED, funnelData)

export const trackWhopEmailPhoneCapture = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_EMAIL_PHONE_CAPTURED, funnelData)

export const trackWhopDQPage = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_QUIZ_REJECTED, funnelData)

export const trackWhopQuizCompleted = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_QUIZ_COMPLETED, funnelData)

export const trackWhopAddPaymentInfo = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_ADD_PAYMENT_INFO, funnelData)

export const trackWhopAddPaymentInfoUpsell = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_ADD_PAYMENT_INFO_UPSELL, funnelData)

export const trackWhopProductSelection = (funnelData = {}) => {
  const {
    selected_plan,
    plan_id,
    amount,
    total_prescription_price,
    selected_product_price,
    display_order_total,
    chosen_medicine_price,
    ...rest
  } = funnelData
  return triggerWhopEvent(WHOP_EVENT_PRODUCT_SELECTION, rest)
}

export const trackWhopProductSelectionUpsell = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_PRODUCT_SELECTION_UPSELL, funnelData)

export const trackWhopPlanSelection = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_PLAN_SELECTION, funnelData)

export const trackWhopPlanSelectionUpsell = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_PLAN_SELECTION_UPSELL, funnelData)

export const trackWhopPurchase = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_PURCHASE, funnelData)

export const trackWhopPurchaseUpsell = (funnelData) =>
  triggerWhopEvent(WHOP_EVENT_PURCHASE_UPSELL, funnelData)

export default {
  triggerWhopEvent,
  buildWhopEventPayload,
  rememberWhopUpsellProductName,
  rememberWhopUpsellAmount,
  rememberWhopUpsellPlan,
  resolveWhopUpsellProductName,
  resolveWhopUpsellAmount,
  resolveWhopUpsellPlan,
  trackWhopStartQuiz,
  trackWhopEmailPhoneCapture,
  trackWhopDQPage,
  trackWhopQuizCompleted,
  trackWhopAddPaymentInfo,
  trackWhopAddPaymentInfoUpsell,
  trackWhopProductSelection,
  trackWhopProductSelectionUpsell,
  trackWhopPlanSelection,
  trackWhopPlanSelectionUpsell,
  trackWhopPurchase,
  trackWhopPurchaseUpsell,
}
